<template>
  <div class="view-wrapper">
    <!-- Top-right single global grand total + toggle control bar -->
    <div class="top-bar-controls">
      <div class="global-grand-total">
        Total: {{ globalGrandTotal.toLocaleString() }}
      </div>
    </div>

    <!-- Single Floating Size Slider -->
    <div class="size-controls" v-if="!showLoading">
      <label for="tag-size-slider" class="size-label">Size</label>
      <input
        id="tag-size-slider"
        type="range"
        :min="minSize"
        :max="maxSize"
        :step="stepSize"
        v-model.number="tagSize"
        class="size-slider"
      />
    </div>

    <!-- Spinner Overlay / State -->
    <div v-if="showLoading" class="loading-state">
      <div class="spinner-container">
        <span class="modern-spinner"></span>
        <p class="loading-text">Loading tags...</p>
      </div>
    </div>

    <!-- Tags Grid -->
    <Transition name="fade">
      <div v-if="isReady" class="tagsContainer">
        <div
          style="margin: 3rem"
          v-for="(type, key) in extractedTags"
          :key="key"
        >
          <DisplayTagsVue
            @click="goToEmails(type.tag)"
            :size="tagSize"
            :tags="[
              {
                type: type.tag,
                custom: type.custom,
                emoji: type.desc,
                desc: type.count,
              },
            ]"
            :hoverAndClickStyling="true"
            titleClass="insights-caption-override"
          />
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import DisplayTagsVue from "./DisplayTags.vue";

const store = useStore();
const router = useRouter();

const isLoading = ref(true);

const extractedTags = computed(() => store.getters.extractedTags || []);

const hasData = computed(() => {
  const data = extractedTags.value;
  return data && (Array.isArray(data) ? data.length > 0 : Object.keys(data).length > 0);
});

// Single global grand total calculation across extractedTags
const globalGrandTotal = computed(() => {
  const data = extractedTags.value;
  if (!data) return 0;
  const items = Array.isArray(data) ? data : Object.values(data);
  const numericSum = items.reduce((sum, item) => {
    const val = Number(item.count ?? item.desc);
    return sum + (isNaN(val) ? 0 : val);
  }, 0);
  return numericSum > 0 ? numericSum : items.length;
});

const showLoading = computed(() => isLoading.value || !hasData.value);
const isReady = computed(() => !isLoading.value && hasData.value);

const maxSize = 160;
const minSize = 60;
const stepSize = 10;
const tagSize = ref(160);

onMounted(async () => {
  isLoading.value = true;
  try {
    await store.dispatch("emailModelTags");
  } catch (error) {
    console.error("Error loading model tags:", error);
  } finally {
    isLoading.value = false;
  }
});

function goToEmails(type) {
  store.commit("tagsFilter", "");
  store.commit("clearSearchResults");
  store.commit("setSearchText", type.toLowerCase());
  router.push("emailkb");
}
</script>

<style scoped>
.view-wrapper {
  position: relative;
  width: 100%;
  padding-top: 3.5rem;
}

.top-bar-controls {
  position: absolute;
  top: 1rem;
  right: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  z-index: 9998;
}

.toggle-btn {
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  cursor: pointer;
  color: #334155;
}

.global-grand-total {
  font-size: 0.85rem;
  font-weight: 600;
  color: #333333;
  background-color: rgba(0, 184, 163, 0.15);
  padding: 4px 12px;
  border-radius: 9999px;
  letter-spacing: 0.02em;
}

.size-controls {
  position: fixed;
  bottom: 1rem;
  left: 1.5rem;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(6px);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  user-select: none;
  z-index: 9999;
}

.size-label {
  font-size: 12px;
  font-weight: 500;
  color: #6c757d;
}

.size-slider {
  width: 80px;
  height: 4px;
  cursor: pointer;
  accent-color: #b86200;
}

.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50vh;
  color: #64748b;
  font-size: 14px;
}

.spinner-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.modern-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid rgba(184, 98, 0, 0.15);
  border-top-color: #b86200;
  border-radius: 50%;
  animation: spin 0.8s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

.loading-text {
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
  letter-spacing: 0.01em;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.tagsContainer {
  display: flex;
  flex-wrap: wrap;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

:deep(.insights-caption-override) {
  max-width: 300px;
  white-space: pre-line;
  word-break: break-word;
  overflow-wrap: break-word;
  font-size: 13px;
  line-height: 1.5;
  text-align: left;
  padding: 10px 14px;
  background-color: #f8fafc;
  color: #1e293b;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.08);
}
</style>